import * as React from 'react';
import { useEffect, useState } from 'react';
import IconButton from '@mui/joy/IconButton';
import Table from '@mui/joy/Table';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import Box from "@mui/joy/Box";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { CircularProgress } from "@mui/joy";
import GeneralApiService from "../../axios/Axios";
import ResponsiveTableWrapper from "../ui/ResponsiveTableWrapper";

interface RowData {
    amount: string;
    new_balance: string;
    created_at: string;
    type: string;
    service: string;
    id: number;
    customer_name?: string;
    created_by_username?: string;
}

function formatDate(old_date: string | Date): string {
    const date = new Date(old_date);
    if (!isNaN(date.getTime())) {
        return date.toLocaleString('en-US', {
            weekday: 'long', year: 'numeric', month: 'long',
            day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'
        });
    }
    return 'No Valid Date';
}

function Row({ row }: { row: RowData }) {
    const isRefund = row.service === 'Lead Conversion Refund';
    return (
        <tr style={{ width: '100%', background: isRefund ? 'rgba(13,255,0,0.09)' : '#ff00002b' }}>
            <td>{row.id}</td>
            <td>{formatDate(row.created_at)}</td>
            <td>{row.customer_name || '-'}</td>
            <td>{row.created_by_username || '-'}</td>
            <td>£{row.amount}</td>
            <td>{row.service}</td>
            <td>{row.type}</td>
        </tr>
    );
}

const mapRawDataToRow = (data: any[]): RowData[] => {
    return data.map(item => ({
        amount: item.amount.toFixed(2),
        created_at: item.created_at,
        type: item.type,
        service: item.service,
        id: item.id,
        new_balance: item.new_balance,
        customer_name: item.customer_name,
        created_by_username: item.created_by_username,
    }));
};

function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number; }) {
    return `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`;
}

export default function LeadConversionsTable() {
    const [loadedData, setLoadingData] = useState(false);
    const [rows, setRows] = useState<RowData[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [pageCount, setPageCount] = useState(0);

    const getLabelDisplayedRowsTo = () => {
        return Math.min(pageCount, (page + 1) * rowsPerPage);
    };

    useEffect(() => {
        setLoadingData(false);
        const fetchData = async () => {
            try {
                const leadConversions = await GeneralApiService.getAllLeadConversions(page + 1);
                setRows(mapRawDataToRow(leadConversions.results || []));
                setPageCount(leadConversions.count || 0);
            } catch (error) {
                console.error("Failed to fetch lead conversions:", error);
                setRows([]);
                setPageCount(0);
            } finally {
                setLoadingData(true);
            }
        };
        fetchData();
    }, [page]);

    return (
        <ResponsiveTableWrapper minWidth={800}>
            <Table hoverRow aria-label="lead conversions table">
                <thead>
                <tr>
                    <th>ID</th>
                    <th style={{ width: '300px' }}>Date</th>
                    <th>Customer</th>
                    <th>Salesperson</th>
                    <th>Amount</th>
                    <th>Service</th>
                    <th>Type</th>
                </tr>
                </thead>
                <tbody>
                {loadedData ? (
                    rows.map((row) => (
                        <Row key={row.id} row={row} />
                    ))
                ) : (
                    <tr><td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}><CircularProgress /></td></tr>
                )}
                </tbody>
                <tfoot>
                <tr>
                    <td colSpan={5}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                            <Typography textAlign="center" sx={{ minWidth: 80 }}>
                                {labelDisplayedRows({
                                    from: pageCount === 0 ? 0 : page * rowsPerPage + 1,
                                    to: getLabelDisplayedRowsTo(),
                                    count: pageCount,
                                })}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <IconButton size="sm" color="neutral" variant="outlined" disabled={page === 0} onClick={() => setPage(page - 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowLeft /></IconButton>
                                <IconButton size="sm" color="neutral" variant="outlined" disabled={page >= Math.ceil(pageCount / rowsPerPage) - 1} onClick={() => setPage(page + 1)} sx={{ bgcolor: 'background.surface' }}><KeyboardArrowRight /></IconButton>
                            </Box>
                        </Box>
                    </td>
                </tr>
                </tfoot>
            </Table>
        </ResponsiveTableWrapper>
    );
}

